import {filter, map, transform} from 'lodash';
import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {userSettingsApi} from 'api';
import {AssignmentSelectors, PredefinedNotifierType} from 'components';
import {UserContext} from 'context';
import {useApiData} from 'hooks';
import {Option} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {InteractiveChatAction} from 'types';
import {interactiveChatUtils} from 'utils/interactive-chat';
import {buildTranslationKey} from 'utils/transform';

import {LiveHelpAutoReply} from './LiveHelpAutoReply';
import {LiveHelpAvatarWelcome} from './LiveHelpAvatarWelcome';
import {LiveHelpDeescalate} from './LiveHelpDeescalate';
import {LiveHelpWelcome} from './LiveHelpWelcome';
import {mapper} from './mapper';
import {FormFields, LiveHelpAutoReplyFields, LiveHelpMessagesFields, MessageType, SectionName} from './types';

const T_KEYS_MESSAGES = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.LIVE_HELP.MESSAGES;

export const LiveHelp = () => {
    const {t} = useTranslation();
    const {settings: {featuresAvailability}} = useContext(UserContext);

    const [selectedGroupId, setSelectedGroupId] = useState<string>(null);
    const [initialValues, setInitialValues] = useState<FormFields>({
        welcome: {messages: []},
        avatarWelcome: {messages: []},
        deescalate: {messages: []},
        autoReply: {
            agentsAvailable: {messages: []},
            agentsUnavailable: {messages: []}
        }
    });

    const {send: fetchLiveHelpSettings, data: liveHelpSettings, loading: fetchLoading} =
        useApiData(userSettingsApi.getLiveHelp, {notificationType: PredefinedNotifierType.FetchError});

    const updateLiveHelp = useCallback((section, data) => userSettingsApi.updateLiveHelp(data).then((result) => ({section, result})), []);

    const updateInitialValues = useCallback(
        ({section, result}) => setInitialValues((prev) => {
            const formFields = mapper.fromSettings(result);
            return {...prev, [section]: formFields[section]};
        }),
        [setInitialValues]);

    const {send: updateLiveHelpWelcomeSettings, loading: updateWelcomeLoading} =
        useApiData(updateLiveHelp, {
            notificationType: PredefinedNotifierType.Save,
            onSuccess: updateInitialValues
        });

    const {send: updateLiveHelpAvatarWelcomeSettings, loading: updateAvatarWelcomeLoading} =
        useApiData(updateLiveHelp, {
            notificationType: PredefinedNotifierType.Save,
            onSuccess: updateInitialValues
        });

    const {send: updateLiveHelpDeescalateSettings, loading: updateDeescalateLoading} =
        useApiData(updateLiveHelp, {
            notificationType: PredefinedNotifierType.Save,
            onSuccess: updateInitialValues
        });

    const {send: updateLiveHelpAutoReplySettings, loading: updateAutoReplyLoading} =
        useApiData(updateLiveHelp, {
            notificationType: PredefinedNotifierType.Save,
            onSuccess: updateInitialValues
        });

    const handleWelcomeSubmit = useCallback((values: LiveHelpMessagesFields | LiveHelpAutoReplyFields) => {
        updateLiveHelpWelcomeSettings(SectionName.Welcome, {
            groupId: selectedGroupId,
            liveHelp: mapper.toPartialSettings(SectionName.Welcome, values)
        });
    }, [updateLiveHelpWelcomeSettings, selectedGroupId]);

    const handleAvatarWelcomeSubmit = useCallback((values: LiveHelpMessagesFields | LiveHelpAutoReplyFields) => {
        updateLiveHelpAvatarWelcomeSettings(SectionName.AvatarWelcome, {
            groupId: selectedGroupId,
            liveHelp: mapper.toPartialSettings(SectionName.AvatarWelcome, values)
        });
    }, [updateLiveHelpAvatarWelcomeSettings, selectedGroupId]);

    const handleDeescalateSubmit = useCallback((values: LiveHelpMessagesFields | LiveHelpAutoReplyFields) => {
        updateLiveHelpDeescalateSettings(SectionName.Deescalate, {
            groupId: selectedGroupId,
            liveHelp: mapper.toPartialSettings(SectionName.Deescalate, values)
        });
    }, [updateLiveHelpDeescalateSettings, selectedGroupId]);

    const handleAutoReplySubmit = useCallback((values: LiveHelpMessagesFields | LiveHelpAutoReplyFields) => {
        updateLiveHelpAutoReplySettings(SectionName.AutoReply, {
            groupId: selectedGroupId,
            liveHelp: mapper.toPartialSettings(SectionName.AutoReply, values)
        });
    }, [updateLiveHelpAutoReplySettings, selectedGroupId]);

    const messageTypeOptions = useMemo(() => Object.values(MessageType).map((messageType) => {
        const label = T_KEYS_MESSAGES[buildTranslationKey(messageType)];
        return {
            id: messageType,
            name: label ? t(label) : messageType
        };
    }), [t]);

    const interactiveChatOptions: Option[] = useMemo(() =>
        map(liveHelpSettings?.widget?.interactiveChat, interactiveChatUtils.buildInteractiveChatOption), [liveHelpSettings]);

    const avatarWelcomeInteractiveChatOptions = useMemo(() =>
        transform(liveHelpSettings?.widget?.interactiveChat, (acc, {value, id}) => {
            if ([
                InteractiveChatAction.ChatGpt,
                InteractiveChatAction.Claude,
                InteractiveChatAction.Mastra,
                InteractiveChatAction.Watsonx,
                InteractiveChatAction.InternalLiveAgent,
            ].includes(value?.action)) {
                acc.push(interactiveChatUtils.buildInteractiveChatOption({id, value}));
            }
        }, []), [liveHelpSettings]);

    useEffect(() => {
        fetchLiveHelpSettings(selectedGroupId);
    }, [fetchLiveHelpSettings, selectedGroupId]);

    useEffect(() => {
        setInitialValues(mapper.fromSettings(liveHelpSettings));
    }, [liveHelpSettings]);

    return (
        <Fragment>
            <div className='tw-flex tw-my-4 tw-items-end -md:tw-flex-col md:tw-justify-between'>
                <AssignmentSelectors
                    selectedGroupId={selectedGroupId}
                    onChange={setSelectedGroupId}
                >
                    {({renderGroup}) => (
                        <div className='tw-w-full md:tw-w-64 tw-flex-shrink-1'>{renderGroup()}</div>
                    )}
                </AssignmentSelectors>
            </div>
            <LiveHelpWelcome
                initialValues={initialValues.welcome}
                loading={fetchLoading || updateWelcomeLoading}
                interactiveChatOptions={interactiveChatOptions}
                messageTypeOptions={messageTypeOptions}
                onSubmit={handleWelcomeSubmit}
            />
            {featuresAvailability.avatar &&
                <LiveHelpAvatarWelcome
                    initialValues={initialValues.avatarWelcome}
                    loading={fetchLoading || updateAvatarWelcomeLoading}
                    interactiveChatOptions={avatarWelcomeInteractiveChatOptions}
                    messageTypeOptions={filter(messageTypeOptions, ({id}) => id === MessageType.InteractiveChat)}
                    onSubmit={handleAvatarWelcomeSubmit}
                />}
            {featuresAvailability.deescalation &&
                <LiveHelpDeescalate
                    initialValues={initialValues.deescalate}
                    loading={fetchLoading || updateDeescalateLoading}
                    interactiveChatOptions={avatarWelcomeInteractiveChatOptions}
                    messageTypeOptions={filter(messageTypeOptions, ({id}) => id === MessageType.InteractiveChat)}
                    onSubmit={handleDeescalateSubmit}
                />}
            <LiveHelpAutoReply
                initialValues={initialValues.autoReply}
                loading={fetchLoading || updateAutoReplyLoading}
                interactiveChatOptions={interactiveChatOptions}
                messageTypeOptions={messageTypeOptions}
                onSubmit={handleAutoReplySubmit}
            />
        </Fragment>
    );
};